import Head from 'expo-router/head';
import { ScrollView } from 'react-native';
import { getTokenValue, Text, YStack } from 'tamagui';

import EventDetailMeta from '@/components/EventDetailMeta';
import type { Event } from '@/src/api/eventsApi';

/**
 * ── /components/event-detail-meta ────────────────────────────────────────
 *
 * EventDetailMeta — the title/date/venue card at the top of the event-detail
 * screen (`app/events/[id]/index.tsx:524`, right below the hero carousel).
 * The component owns its own date formatting per the house rule: it takes
 * the raw `Event` (raw `startDatetime`s and all) and runs them through
 * `formatSlotSummary`/`formatDateTime` itself — every fixture below passes
 * raw ISO strings, never a pre-formatted label.
 *
 * Two things in the real component are worth flagging up front, since
 * they're easy to miss reading the preview alone:
 *   - its `borderWidth` prop is commented OUT in the source, so despite the
 *     `borderColor` prop still being set, the card currently renders with NO
 *     visible border — just a rounded, flat `$background` fill.
 *   - it never sets a `p` (padding) — its own docstring claims the ticket
 *     blocks' "$md-pad box", but the code doesn't pad at all. Both look like
 *     a stale docstring outrunning the code, not this preview's doing; not
 *     fixed here since that's the component's call, not the gallery's.
 *
 * `Event` carries plenty the component never reads (`priceFrom`, `images`,
 * `kind`, `slots`, …) — only `title`, `venue`, and the bookable-slot ends
 * (falling back to `slots`' own ends once there's no bookable run left) drive
 * what's on screen. Fixtures below still fill the rest in (the type requires
 * it, via `UNREAD_FIELDS`) but don't vary it, per the "model only rendered
 * state" rule — there's no branch to demonstrate.
 * ─────────────────────────────────────────────────────────────────────────
 */

// Approximates the event screen's real content width (viewport minus its
// `px="$lg"` gutters) so wrap/overflow cases below (long title, long venue)
// read the way they will on an actual phone, not artificially content-hugging
// the way an unconstrained `items="flex-start"` YStack would.
const PREVIEW_WIDTH = 360;

// Fields every fixture needs but this component never branches on — see the
// docstring above. Spread into each event so every case stays focused on the
// one field it's actually demonstrating.
const UNREAD_FIELDS = {
  id: 'preview-event',
  images: [],
  kind: 'ticketed' as const,
  category: 'normal' as const,
  isSoldOut: false,
  externalUrl: null,
  orgDetails: null,
  priceFrom: { amountInMinorUnits: 240000, currency: 'INR', display: '₹2,400' },
  isInPast: false,
  termsAndConditions: '',
  description: '',
  isRsvpEvent: false,
  bookings: [],
  // Only read once `nextBookableSlot` is null (see NO_RUN_LEFT_EVENT below);
  // every other fixture has a bookable slot, so this default is never hit.
  slots: [],
};

// An ISO datetime N days out, at a fixed evening time — LOCAL, no trailing Z
// (`parseISO` in dateFormatter.ts reads a bare string as local time; a
// `toISOString()` value would shift to UTC and could land on the wrong day
// near midnight). Only the "Today"/"Tomorrow" cases below need this — every
// other case is a fixed date, since the point there is the calendar SHAPE
// (same day vs spanning days, this year vs next), not "what day is it now".
function slotIso(daysOut: number, hour = 19) {
  const d = new Date();
  d.setDate(d.getDate() + daysOut);
  d.setHours(hour, 30, 0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(hour)}:30:00`;
}

// One slot (nextBookableSlot === lastBookableSlot) → `formatDateTime`'s
// `relativeDay` label: "Today"/"Tomorrow" while close, else the weekday+month,
// plus a year suffix once it's no longer THIS year.
const SINGLE_SLOT_CASES: { id: string; label: string; event: Event }[] = [
  {
    id: 'today',
    label:
      'Today — relativeDay() reads it off the real clock, not a fixture date',
    event: {
      ...UNREAD_FIELDS,
      title: 'Rooftop Jazz Night',
      nextBookableSlot: {
        startDatetime: slotIso(0),
        endDatetime: slotIso(0, 21),
      },
      lastBookableSlot: {
        startDatetime: slotIso(0),
        endDatetime: slotIso(0, 21),
      },
      venue: {
        name: 'Sky Deck, Indiranagar',
        googleMapsUrl: 'https://maps.google.com',
      },
    },
  },
  {
    id: 'tomorrow',
    label: 'Tomorrow',
    event: {
      ...UNREAD_FIELDS,
      title: 'Sunset Wine Tasting',
      nextBookableSlot: {
        startDatetime: slotIso(1),
        endDatetime: slotIso(1, 21),
      },
      lastBookableSlot: {
        startDatetime: slotIso(1),
        endDatetime: slotIso(1, 21),
      },
      venue: {
        name: 'Vineyard Terrace, Whitefield',
        googleMapsUrl: 'https://maps.google.com',
      },
    },
  },
  {
    id: 'this-year',
    label:
      'Further out, still this year — weekday + day + month, no year suffix',
    event: {
      ...UNREAD_FIELDS,
      title: 'Sunset Rooftop Sundowner with Live Sitar',
      nextBookableSlot: {
        startDatetime: '2026-11-20T19:30:00',
        endDatetime: '2026-11-20T22:30:00',
      },
      lastBookableSlot: {
        startDatetime: '2026-11-20T19:30:00',
        endDatetime: '2026-11-20T22:30:00',
      },
      venue: {
        name: 'Sky Deck, Indiranagar',
        googleMapsUrl: 'https://maps.google.com',
      },
    },
  },
  {
    id: 'other-year',
    label: 'Next year — isThisYear() flips false, so the year gets appended',
    event: {
      ...UNREAD_FIELDS,
      title: 'Spring Food Festival',
      nextBookableSlot: {
        startDatetime: '2027-03-10T18:00:00',
        endDatetime: '2027-03-10T22:00:00',
      },
      lastBookableSlot: {
        startDatetime: '2027-03-10T18:00:00',
        endDatetime: '2027-03-10T22:00:00',
      },
      venue: {
        name: 'Cubbon Park Bandstand',
        googleMapsUrl: 'https://maps.google.com',
      },
    },
  },
];

// Several slots (`nextBookableSlot` !== `lastBookableSlot`) → `formatSlotSummary`
// branches on whether the two ends fall on the SAME calendar day: one day gets
// the compact "date · Multiple slots" tag, several days get an explicit
// "date to date" span (note: "to", not an en dash — matches the current source).
const MULTI_SLOT_CASES: { id: string; label: string; event: Event }[] = [
  {
    id: 'multi-same-day',
    label: 'Several slots, one day — "date · Multiple slots"',
    event: {
      ...UNREAD_FIELDS,
      title: 'An Evening of Sufi Qawwali & Slow-Cooked Awadhi Dinner',
      nextBookableSlot: {
        startDatetime: '2026-10-01T13:30:00',
        endDatetime: '2026-10-01T15:30:00',
      },
      lastBookableSlot: {
        startDatetime: '2026-10-01T19:30:00',
        endDatetime: '2026-10-01T22:30:00',
      },
      venue: {
        name: 'Qawwali Courtyard, Shivaji Nagar',
        googleMapsUrl: 'https://maps.google.com',
      },
    },
  },
  {
    id: 'multi-spanning-days',
    label:
      'Slots spanning days — "date to date", no relative day once it spans',
    event: {
      ...UNREAD_FIELDS,
      title: 'Pizza-Making Studio',
      nextBookableSlot: {
        startDatetime: '2026-10-10T18:00:00',
        endDatetime: '2026-10-10T20:00:00',
      },
      lastBookableSlot: {
        startDatetime: '2026-10-12T18:00:00',
        endDatetime: '2026-10-12T20:00:00',
      },
      venue: {
        name: 'La Cucina Kitchen Studio, HSR Layout',
        googleMapsUrl: 'https://maps.google.com',
      },
    },
  },
];

// Real wire lies about this being non-null: `ShortEvent.nextBookableSlot` is
// TYPED as always present, but EventDetailMeta falls back to "Already over!"
// once there's no bookable run left AND no slots to recover it from — a
// truly slot-less/cancelled event that never got flagged `isInPast`.
// EventCard's identical ternary (components/EventCard.tsx:144-146) confirms
// this is a real, repeated pattern, not a one-off defensive check. Forcing it
// here needs a cast since `Event`'s own type won't allow `null` on its own.
const NO_RUN_LEFT_EVENT: Event = {
  ...UNREAD_FIELDS,
  title: 'Winter Supper Club',
  isInPast: true,
  nextBookableSlot: null as unknown as Event['nextBookableSlot'],
  lastBookableSlot: {
    startDatetime: '2025-12-14T19:30:00',
    endDatetime: '2025-12-14T22:30:00',
  },
  venue: {
    name: 'The Fireside Room, Koramangala',
    googleMapsUrl: 'https://maps.google.com',
  },
};

// Long title — PSText.Title has no numberOfLines clamp here, so it wraps
// freely above the date line; worth eyeballing that the card grows instead of
// the title running into the border.
const LONG_TITLE_EVENT: Event = {
  ...UNREAD_FIELDS,
  title:
    "Bangladesh: Humiliation, Carnage, Liberation, Chaos - An Audiovisual Chronicle of Bangladesh's Liberation War in 1971",
  nextBookableSlot: {
    startDatetime: '2026-11-08T19:30:00',
    endDatetime: '2026-11-08T22:30:00',
  },
  lastBookableSlot: {
    startDatetime: '2026-11-08T19:30:00',
    endDatetime: '2026-11-08T22:30:00',
  },
  venue: {
    name: 'Bangalore International Centre, Domlur',
    googleMapsUrl: 'https://maps.google.com',
  },
};

// Long venue name — the venue row is an XStack (icon + PSLinkButton) with no
// `flexWrap` and no `numberOfLines` on the link text, so unlike the title
// above, nothing here is set up to wrap; watch whether a long name pushes
// past the card's right edge instead of breaking to a second line.
const LONG_VENUE_EVENT: Event = {
  ...UNREAD_FIELDS,
  title: 'Community Morning',
  nextBookableSlot: {
    startDatetime: '2026-11-15T09:00:00',
    endDatetime: '2026-11-15T11:00:00',
  },
  lastBookableSlot: {
    startDatetime: '2026-11-15T09:00:00',
    endDatetime: '2026-11-15T11:00:00',
  },
  venue: {
    name: 'The Grand Heritage Botanical Gardens & Amphitheatre, Old Airport Road, Bangalore',
    googleMapsUrl: 'https://maps.google.com',
  },
};

export default function EventDetailMetaPreview() {
  return (
    <>
      <Head>
        <title>EventDetailMeta · Putting Scene</title>
      </Head>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack items="flex-start" gap="$lg" p="$lg">
          {/* One slot: the date line is `formatDateTime`'s single-datetime path
            (relativeDay + time). Today/Tomorrow are LIVE relative to the real
            clock via slotIso(); the other two are fixed dates chosen to land
            on the this-year/next-year boundary the year-suffix logic cares
            about. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              one bookable slot — Today / Tomorrow (relative) vs a plain date,
              this year and next (absolute, year suffix only when it&apos;s not
              the current year)
            </Text>
            <YStack width={PREVIEW_WIDTH} gap="$md">
              {SINGLE_SLOT_CASES.map((c) => (
                <YStack key={c.id} gap="$xs">
                  <Text
                    fontFamily="$body"
                    fontSize="$small"
                    color="$textDarkMuted"
                  >
                    {c.label}
                  </Text>
                  <EventDetailMeta event={c.event} />
                </YStack>
              ))}
            </YStack>
          </YStack>

          {/* Multiple slots: the date line switches to `formatSlotSummary`'s
            two-end comparison — same day collapses to one date + a "Multiple
            slots" tag, different days spell out both ends joined by "to". */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              several bookable slots — same day vs spanning days
            </Text>
            <YStack width={PREVIEW_WIDTH} gap="$md">
              {MULTI_SLOT_CASES.map((c) => (
                <YStack key={c.id} gap="$xs">
                  <Text
                    fontFamily="$body"
                    fontSize="$small"
                    color="$textDarkMuted"
                  >
                    {c.label}
                  </Text>
                  <EventDetailMeta event={c.event} />
                </YStack>
              ))}
            </YStack>
          </YStack>

          {/* No bookable run left: `nextBookableSlot` is falsy, so the date line
            skips `formatSlotSummary` entirely and reads "Already over!" — the
            title and venue row are unaffected, since only the date line
            branches on this. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              nextBookableSlot null — &quot;Already over!&quot; instead of a
              date (finished, cancelled, or slot-less events)
            </Text>
            <YStack width={PREVIEW_WIDTH}>
              <EventDetailMeta event={NO_RUN_LEFT_EVENT} />
            </YStack>
          </YStack>

          {/* Long title vs long venue name — same card, two different wrap
            behaviours (see the const comments above for why they differ). */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              very long title — wraps freely, card grows to fit
            </Text>
            <YStack width={PREVIEW_WIDTH}>
              <EventDetailMeta event={LONG_TITLE_EVENT} />
            </YStack>
          </YStack>
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              very long venue name — the venue row doesn&apos;t wrap; watch for
              overflow past the card&apos;s edge
            </Text>
            <YStack width={PREVIEW_WIDTH}>
              <EventDetailMeta event={LONG_VENUE_EVENT} />
            </YStack>
          </YStack>
        </YStack>
      </ScrollView>
    </>
  );
}
